import { Link } from '@zoopla-eng/zds-components/link';
import { Bulleted } from '@zoopla-eng/zds-components/lists/bulleted';
import { Stack } from '@zoopla-eng/zds-components/stack';
import { NewHomesBody } from '../NewHomesBody';
import { NewHomesBodyBold } from '../NewHomesBodyBold';

export type NewHomesFaqContent = typeof newHomesFaqContent;

export const newHomesFaqContent = [
    {
        label: 'The benefits of buying a new-build home',
        value: '1',
        component: (
            <Bulleted data-testid="new-homes-faq-answer-1">
                <li>
                    There's no onward chain, you can move in as soon as your
                    home's ready
                </li>
                <li>
                    Everything’s sparkling new, no renovation budget required
                </li>
                <li>
                    If you’re buying off-plan, you get to make the decisions on
                    the style of kitchen units, tiles choices and flooring. Some
                    builders will also allow you to upgrade your specification.
                </li>
                <li>
                    Financial schemes are available to buy new builds, including
                    Help to Buy, Shared Ownership, Deposit Unlock and First
                    Homes
                </li>
                <li>New-build homes come with a 10 year warranty</li>
                <li>
                    And they're eco-friendly, using around 60% less energy than
                    period homes
                </li>
                <li>
                    Most new homes are fitted with modern locks on all doors and
                    windows, with alarms and entryphone systems installed in
                    many properties as standard
                </li>
            </Bulleted>
        )
    },
    {
        label: 'Are new-build homes better than older homes?',
        value: '2',
        component: (
            <div data-testid="new-homes-faq-answer-2">
                <Stack rowGap="$S300">
                    <NewHomesBody>
                        The main benefits for buying a new-build home versus an
                        older home are:
                    </NewHomesBody>
                    <Bulleted>
                        <li>
                            New-builds come with 10-year structural warranties,
                            which gives you protection and peace of mind
                        </li>
                        <li>
                            They achieve top eco credentials, using the latest
                            A+ rated white goods to reduce energy consumption.
                            And that means lower fuel bills for you
                        </li>
                        <li>
                            Double or triple glazing, low energy lighting and
                            dual flushes to reduce water usage are installed in
                            many properties as standard
                        </li>
                        <li>
                            If you’re buying off plan, you can choose your
                            fixtures, fittings and flooring
                        </li>
                        <li>
                            Schemes are available exclusively for new-build
                            homes, including{' '}
                            <Link
                                href="/schemes/help-to-buy/"
                                title="Help to Buy: Equity Loan at a glance"
                            >
                                Help to Buy
                            </Link>
                            ,{' '}
                            <Link
                                href="/schemes/shared-ownership/"
                                title="What is Shared Ownership?"
                            >
                                Shared Ownership
                            </Link>{' '}
                            and{' '}
                            <Link
                                href="/discover/buying/what-is-deposit-unlock/"
                                title="What is Deposit Unlock?"
                            >
                                Deposit Unlock
                            </Link>
                        </li>
                    </Bulleted>
                    <NewHomesBody>
                        <Link
                            href="/discover/buying/pros-and-cons-of-a-new-build-home/"
                            title="New-build homes versus older homes"
                        >
                            New-build homes versus older homes
                        </Link>
                    </NewHomesBody>
                </Stack>
            </div>
        )
    },
    {
        label: 'Do new-build homes keep their value?',
        value: '3',
        component: (
            <div data-testid="new-homes-faq-answer-3">
                <Stack rowGap="$S300">
                    <NewHomesBody>
                        With any home, traditional or new, the rate at which it
                        rises in value will depend on each individual property.
                    </NewHomesBody>
                    <NewHomesBody>
                        Things that might affect a propert’s value include:
                    </NewHomesBody>
                    <Bulleted>
                        <li>
                            Renovations being done to the home, including
                            extensions
                        </li>
                        <li>If the area the property is ‘on the up’</li>
                        <li>
                            If new transport links are developed near to the
                            property
                        </li>
                        <li>
                            If schools near to the home are rated Outstanding by
                            Ofsted
                        </li>
                        <li>
                            If high demand develops for the area the property is
                            in
                        </li>
                    </Bulleted>
                    <NewHomesBodyBold>
                        Own a new-build home and keen to see how much it’s value
                        has risen?
                    </NewHomesBodyBold>
                    <NewHomesBody>
                        Head over to{' '}
                        <Link
                            href="/home-values/"
                            title="How much is my home worth?"
                        >
                            My Home
                        </Link>
                        , where you can discover your home’s value with an
                        instant online estimate based on powerful market data.
                    </NewHomesBody>
                    <NewHomesBodyBold>
                        Interested in buying a new-build home?
                    </NewHomesBodyBold>
                    <NewHomesBody>
                        Over at our{' '}
                        <Link
                            href="/house-prices/"
                            title="Search house prices by town, road name or postcode"
                        >
                            House Prices
                        </Link>{' '}
                        checker, you can simply pop in the postcode for the home
                        you’re interested in and find out how much it’s
                        estimated to be worth now.
                    </NewHomesBody>
                    <NewHomesBody>
                        We’ll also show you how much each property last sold for
                        - and how much its value has risen over time.
                    </NewHomesBody>
                </Stack>
            </div>
        )
    },
    {
        label: 'Is it hard to sell a new-build home?',
        value: '4',
        component: (
            <div data-testid="new-homes-faq-answer-4">
                <Stack rowGap="$S300">
                    <NewHomesBody>
                        If you own the freehold to your new-build home, selling
                        it will be just as straightforward as selling a
                        traditional home.
                    </NewHomesBody>
                    <NewHomesBody>
                        However, if you bought your home as leasehold property,
                        or through a government scheme like Help to Buy or
                        Shared Ownership, there will be a few extra steps
                        involved in the selling process.
                    </NewHomesBody>
                    <NewHomesBody>Find out more in our guides:</NewHomesBody>
                    <Bulleted>
                        <li>
                            <Link
                                href="https://www.zoopla.co.uk/discover/selling/how-do-you-sell-a-leasehold-property/"
                                title="How do you sell a leasehold property?"
                            >
                                How do you sell a leasehold property?
                            </Link>
                        </li>
                        <li>
                            <Link
                                href="https://www.zoopla.co.uk/discover/selling/how-to-sell-a-shared-ownership-property/"
                                title="How to sell a Shared Ownership property"
                            >
                                How to sell a Shared Ownership property
                            </Link>
                        </li>
                        <li>
                            <Link
                                href="https://www.zoopla.co.uk/discover/selling/how-to-sell-a-help-to-buy-property/"
                                title="How to sell a Help to Buy property"
                            >
                                How to sell a Help to Buy property
                            </Link>
                        </li>
                    </Bulleted>
                </Stack>
            </div>
        )
    },
    {
        label: 'Can you buy a new-build home with Help to Buy?',
        value: '5',
        component: (
            <div data-testid="new-homes-faq-answer-5">
                <Stack rowGap="$S300">
                    <NewHomesBody>
                        Yes, the Help to Buy scheme is exclusively available for
                        new-build homes. It’s a government initiative designed
                        to help first-time buyers step onto the property ladder
                        with a deposit of just 5%.
                    </NewHomesBody>
                    <NewHomesBody>
                        The scheme comes to an end in 2023 so you’d better be
                        quick.
                    </NewHomesBody>
                    <NewHomesBody>
                        Other schemes available for new-build homes include:
                    </NewHomesBody>
                    <Bulleted>
                        <li>
                            <Link
                                href="/schemes/shared-ownership/"
                                title="Shared Ownership at a glance"
                            >
                                Shared Ownership
                            </Link>
                            , where you can buy a share of a new-build home if
                            you’re a first-time buyer
                        </li>
                        <li>
                            <Link
                                href="/discover/buying/what-is-deposit-unlock/"
                                title="What is Deposit Unlock?"
                            >
                                Deposit Unlock
                            </Link>
                            , which enables you to buy a new-build home with a
                            95% mortgage if you’re a first-time buyer or
                            second-stepper
                        </li>
                        <li>
                            And{' '}
                            <Link
                                href="/discover/buying/first-time-buyers-to-get-30-percent-discount-on-first-homes-government-scheme/"
                                title="The First Homes scheme: half-price homes for first-time buyers"
                            >
                                First Homes
                            </Link>
                            , a scheme that offers local first-time buyers and
                            key workers a 30-50% discount on new-build homes.
                        </li>
                    </Bulleted>
                </Stack>
            </div>
        )
    },
    {
        label: 'Are new-build homes more expensive?',
        value: '6',
        component: (
            <div data-testid="new-homes-faq-answer-6">
                <Stack rowGap="$S300">
                    <NewHomesBody>
                        New-build properties are typically more energy efficient
                        and require less maintenance compared to an older
                        property.{' '}
                    </NewHomesBody>
                    <NewHomesBody>
                        For that reason, and many others, some new-build
                        properties may be advertised at a higher asking price
                        compared to similar older properties.
                    </NewHomesBody>
                    <NewHomesBody>
                        A good way to check that you won’t be paying over the
                        odds for a new-build property is to{' '}
                        <Link
                            href="/house-prices/"
                            title="Search house prices by town, road name or postcode"
                        >
                            check recently sold prices
                        </Link>{' '}
                        of similar new-build properties in the same, or similar,
                        area.
                    </NewHomesBody>
                </Stack>
            </div>
        )
    },
    {
        label: 'Can you negotiate on new-build homes?',
        value: '7',
        component: (
            <div data-testid="new-homes-faq-answer-7">
                <Stack rowGap="$S300">
                    <NewHomesBody>
                        Just like buying an older property you can try and
                        negotiate with the housebuilder by making an offer. The
                        worst they can say is no.
                    </NewHomesBody>
                    <NewHomesBody>
                        If they are staying firm on the asking price, there may
                        be other things you can negotiate on such as
                        furnishings, or they could offer to pay your legal fees
                        or stamp duty which could save you a considerable amount
                        of money, but do note that most mortgage lenders will
                        only accept incentives up to a maximum value of 5% of
                        the home.
                    </NewHomesBody>
                </Stack>
            </div>
        )
    }
];
